import { useRef } from "react";
import { cn } from "../../lib/cn";
import { nextRadioIndex } from "../../primitives/radio-navigation";
import { AttentionPanel, PANEL_ROW, panelRowStyle } from "./panel";
import type { AttentionChoice, OpacityMix } from "./types";

/** The card's answer options, as one row each in the slot the signals panel otherwise fills. */
export function AttentionChoices({
  busy = false,
  choices,
  chosen,
  label,
  onChoose,
  opacityMix,
}: {
  busy?: boolean;
  choices: AttentionChoice[];
  chosen?: string | null;
  label?: string;
  onChoose?: (value: string) => void;
  opacityMix: OpacityMix;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const heading = label ?? "Your answer";
  // Without a pick, the first option is the one tab reaches, per WAI-ARIA.
  const activeIndex = Math.max(
    0,
    choices.findIndex((c) => c.value === chosen),
  );
  return (
    <AttentionPanel busy={busy} label={heading} opacityMix={opacityMix}>
      <div role="radiogroup" aria-label={heading} className="grid gap-3">
        {choices.map((choice, index) => {
          const picked = chosen === choice.value;
          return (
            <div key={choice.value} className={cn(PANEL_ROW)} style={panelRowStyle}>
              {/* biome-ignore lint/a11y/useSemanticElements: a native radio cannot carry the row's label-plus-marker layout; button+role+aria-checked is the pattern segmented.tsx already uses for this */}
              <button
                type="button"
                role="radio"
                aria-checked={picked}
                tabIndex={index === activeIndex ? 0 : -1}
                // Not `disabled`:
                aria-disabled={busy || undefined}
                ref={(el) => {
                  refs.current[index] = el;
                }}
                onKeyDown={(event) => {
                  const next = nextRadioIndex(event.key, index, choices.length);
                  if (next === null) return;
                  event.preventDefault();
                  refs.current[next]?.focus();
                }}
                onClick={() => {
                  if (!busy) onChoose?.(choice.value);
                }}
                className="flex min-h-[44px] w-full items-center justify-between gap-3 bg-transparent text-left text-sm transition-colors duration-200 aria-disabled:opacity-60"
                style={{ color: opacityMix(picked ? 100 : 85) }}
              >
                <span className={cn(picked && "font-semibold")}>{choice.label}</span>
                <span
                  aria-hidden="true"
                  className="size-3.5 shrink-0 rounded-full border"
                  style={{
                    borderColor: opacityMix(picked ? 95 : 38),
                    background: picked ? opacityMix(95) : "transparent",
                  }}
                />
              </button>
            </div>
          );
        })}
      </div>
    </AttentionPanel>
  );
}
